import {
  getSessionPhysioOrgIdFromAccessToken,
  isOrgAlignedWithPendingRedirect,
  parseOrgIdFromRelativePath,
} from 'utils/postLoginRedirect';

/**
 * Tokens present in cookies (same semantics as previous inline checks in DirectLogin).
 * @param {string | undefined} accessToken
 * @param {string | undefined} refreshToken
 * @returns {boolean}
 */
export function hasStoredSessionTokens(accessToken, refreshToken) {
  return (
    accessToken !== undefined &&
    refreshToken !== undefined &&
    accessToken !== null &&
    refreshToken !== null
  );
}

/**
 * Pending report deep link includes `org_id` and the session org is not yet aligned with it.
 * Applies to members and Braincare admins: do not auto-navigate with a token from another org.
 * While `activeOrganizationId` is still -1 / unknown, treat as blocked until Redux hydrates.
 * @param {{ pendingPath: string | null, activeOrganizationId: unknown }} params
 * @returns {boolean}
 */
export function isMemberPendingOrgMisaligned({ pendingPath, activeOrganizationId }) {
  if (!pendingPath) {
    return false;
  }
  if (parseOrgIdFromRelativePath(pendingPath) == null) {
    return false;
  }
  if (activeOrganizationId == null || activeOrganizationId === -1) {
    return true;
  }
  return !isOrgAlignedWithPendingRedirect(activeOrganizationId, pendingPath);
}

/**
 * @param {Array<Record<string, unknown>>} organizations
 * @param {unknown} activeOrganizationId — Redux `active_organization.id` (-1 while hydrating).
 * @param {string | undefined | null} [accessToken] — used when `activeOrganizationId` is unknown to
 *   derive current org from JWT `org_id` (physio_org_id) so the “locked” account card still works.
 * @returns {Array<Record<string, unknown> & { login_status: boolean }>}
 */
export function mapOrganizationsToAccountsWithLoginStatus(organizations, activeOrganizationId, accessToken) {
  const activeKnown = activeOrganizationId != null && activeOrganizationId !== -1;
  const jwtPhysioOrgId = activeKnown ? null : getSessionPhysioOrgIdFromAccessToken(accessToken);

  return organizations.map((o) => {
    if (activeKnown) {
      return {
        ...o,
        login_status: String(o.id) === String(activeOrganizationId),
      };
    }
    if (jwtPhysioOrgId != null) {
      const byPhysio =
        o.physio_org_id != null && String(o.physio_org_id) === jwtPhysioOrgId;
      return { ...o, login_status: Boolean(byPhysio) };
    }
    return { ...o, login_status: false };
  });
}

/**
 * After multi-account login: align session to org in pending path when possible.
 * Caller must still guard on accounts length and one-shot ref.
 * @param {{
 *   pendingPath: string | null,
 *   accounts: Array<{ id: unknown, login_status?: boolean }>,
 *   pendingCredentials: { document?: string, password?: string },
 * }} params
 * @returns
 *   | { kind: 'noop' }
 *   | { kind: 'invalid-link' }
 *   | { kind: 'handled-wait-password' }
 *   | { kind: 'handled-missing-credentials' }
 *   | { kind: 'auto-login', account: { id: unknown, login_status?: boolean } }
 */
export function resolveEmailLinkAccountHint({ pendingPath, accounts, pendingCredentials }) {
  const wantOrgId = parseOrgIdFromRelativePath(pendingPath);
  if (wantOrgId == null) {
    return { kind: 'noop' };
  }

  const match = accounts.find((a) => String(a.id) === String(wantOrgId));
  if (!match) {
    return { kind: 'invalid-link' };
  }

  if (!match.login_status) {
    return { kind: 'handled-wait-password' };
  }

  const { document, password } = pendingCredentials;
  if (!document || !password) {
    return { kind: 'handled-missing-credentials' };
  }

  return { kind: 'auto-login', account: match };
}

/**
 * Branching for onLoginSuccess around pending redirect (matches previous if / else if).
 * @param {{ pendingPath: string | null, loginOrganizationId: unknown, skipOrgAlign: boolean }} params
 * @returns {'wrong-org-show-toast' | 'try-pending-navigate' | 'none'}
 */
export function getLoginSuccessPendingRedirectStep({ pendingPath, loginOrganizationId, skipOrgAlign }) {
  if (!pendingPath) {
    return 'none';
  }
  if (skipOrgAlign) {
    return 'try-pending-navigate';
  }
  const orgAlignedLogin = isOrgAlignedWithPendingRedirect(loginOrganizationId, pendingPath);
  if (!orgAlignedLogin) {
    return 'wrong-org-show-toast';
  }
  return 'try-pending-navigate';
}

/**
 * Resolves the email deep-link org PK when that org appears in the accounts list (utility / tests).
 * @param {Array<{ id: unknown }> | undefined} accounts
 * @param {string | null} pendingPath
 * @returns {string | number | null}
 */
export function getLinkTargetOrganizationIdForAccountList(accounts, pendingPath) {
  if (!accounts?.length || pendingPath == null || pendingPath === '') {
    return null;
  }
  const redirectOrgId = parseOrgIdFromRelativePath(pendingPath);
  if (redirectOrgId == null) {
    return null;
  }
  const linkedInList = accounts.some((a) => String(a.id) === String(redirectOrgId));
  return linkedInList ? redirectOrgId : null;
}

/**
 * Whether the picked account is not the organization targeted by the email deep link (`org_id`).
 * Used when closing the password dialog for a different org: offer to discard the pending redirect.
 * @param {string | null} pendingPath
 * @param {unknown} pickedAccountId
 * @returns {boolean}
 */
export function shouldConfirmDiscardBeforeSelectingOrg(pendingPath, pickedAccountId) {
  const wantOrgId = parseOrgIdFromRelativePath(pendingPath);
  if (wantOrgId == null) {
    return false;
  }
  return String(pickedAccountId) !== String(wantOrgId);
}
